/* eslint-disable react/no-unstable-nested-components */
import { FilterTwoTone, PlusCircleTwoTone } from "@ant-design/icons";
import PageTitle from "@component/PageTitle";
import { fetchVendorList, resetVendorSlice, updateVendorListParams } from "@redux/vendorSlice";
import { VendorListItem } from "@page/VendorInfoPage/interface";

import EditScopeModal from "@page/VendorInfoPage/EditScopeModal";
import {
  VendorCategory,
  vendorCategoryMap,
  VendorApplicationStatus,
  tagMap,
  EDITABLE_BASIC_STATUSES,
} from "@page/VendorInfoPage/constant";
import { Button, Col, Row, Select, Space, Table, Tag } from "antd";
import { ColumnsType } from "antd/lib/table";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useSelector } from "src/store";
import styled from "styled-components";
import { TableWrapper } from "src/styles/common";
import VendorFilter from "./VendorFilter";

const { Option } = Select;

const Wrapper = styled.div`
  padding: 25px 20px;
`;
const Text = styled.div`
  white-space: nowrap;
`;
const CustomButton = styled(Button)`
  margin-left: 10px;
`;

export default function VendorListPage() {
  const dispatch = useAppDispatch();
  const { isFetchingVendorList, vendorList, vendorListTotal, vendorListParams } = useSelector(
    (state) => state.vendorSlice,
  );

  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState<VendorListItem | null>(null);

  const { limit, offset } = vendorListParams;
  const currentPage = useMemo(() => Math.floor(offset / limit) + 1, [limit, offset]);

  useEffect(() => {
    dispatch(fetchVendorList());
    return () => {
      dispatch(resetVendorSlice());
    };
  }, [dispatch]);

  const handleSelectChange = (value: number) => {
    dispatch(updateVendorListParams({ limit: value, offset: 0 }));
    dispatch(fetchVendorList());
  };

  const handleView = (record: VendorListItem) => {
    window.open(`/vendor/info/${record.id}`, "_blank");
  };

  const handleEdit = (record: VendorListItem) => {
    setSelectedItem(record);
    setEditModalVisible(true);
  };

  const handleTableChangePage = (page: number) => {
    dispatch(
      updateVendorListParams({
        limit,
        offset: (page - 1) * limit,
      }),
    );
    dispatch(fetchVendorList());
  };

  const columns: ColumnsType<VendorListItem> = [
    {
      title: "流水編號",
      dataIndex: "id",
    },
    {
      title: "廠商名稱",
      dataIndex: "name",
    },
    {
      title: "廠商類別",
      dataIndex: "category",
      render: (value: VendorCategory) => vendorCategoryMap[value] ?? "-",
    },
    {
      title: "申請狀態",
      dataIndex: "applicationStatus",
      render: (value: VendorApplicationStatus) => {
        const tagInfo = value ? tagMap[value] : null;
        const TagIcon = tagInfo?.icon;
        if (!tagInfo) return "-";
        return (
          <Tag icon={TagIcon && <TagIcon />} color={tagInfo.color}>
            {tagInfo.text}
          </Tag>
        );
      },
    },
    {
      title: "建立時間",
      dataIndex: "createdAt",
      render: (createdAt: string) => {
        if (!createdAt) return "-";
        const parsedTime = moment(createdAt, moment.ISO_8601, true);
        return parsedTime.isValid() ? parsedTime.format("YYYY-MM-DD HH:mm") : "-";
      },
    },
    {
      title: "饌元負責人",
      dataIndex: ["owner", "name"],
    },
    {
      title: "",
      fixed: "right",
      render: (_: unknown, record: VendorListItem) => (
        <Space>
          <Button
            type="link"
            size="small"
            onClick={() => handleEdit(record)}
            disabled={!record.sourceId && !EDITABLE_BASIC_STATUSES.includes(record.applicationStatus)}
          >
            編輯
          </Button>
          <Button type="link" size="small" onClick={() => handleView(record)}>
            檢視
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Wrapper>
      <PageTitle title="廠商列表" />
      <Row justify="space-between">
        <Col span={12}>
          <Button
            type="primary"
            icon={<PlusCircleTwoTone twoToneColor="#1890FF" />}
            onClick={() => window.open("/vendor/info/new", "_blank")}
          >
            新增廠商
          </Button>
          <CustomButton icon={<FilterTwoTone />} onClick={() => setShowFilter(!showFilter)}>
            篩選
          </CustomButton>
          <CustomButton href="/vendor/deposit-balance" target="_blank">
            保證金餘額總表
          </CustomButton>
        </Col>
        <Col span={6}>
          <Space>
            <Text>{`總共${vendorListTotal}筆，每頁顯示`}</Text>
            <Select
              value={limit}
              style={{ width: "72px", fontSize: "13px" }}
              onChange={(value) => handleSelectChange(value as number)}
            >
              <Option value={20}>20</Option>
              <Option value={50}>50</Option>
              <Option value={100}>100</Option>
            </Select>
            <span>筆</span>
          </Space>
        </Col>
      </Row>
      {showFilter && <VendorFilter />}
      <TableWrapper padding="15px 0">
        <Table
          columns={columns}
          rowKey="id"
          dataSource={vendorList}
          loading={isFetchingVendorList}
          scroll={{ x: "max-content" }}
          pagination={{
            current: currentPage,
            total: vendorListTotal,
            onChange: handleTableChangePage,
            pageSize: limit,
            showSizeChanger: false,
          }}
        />
      </TableWrapper>
      {selectedItem && (
        <EditScopeModal visible={editModalVisible} vendor={selectedItem} onClose={() => setEditModalVisible(false)} />
      )}
    </Wrapper>
  );
}
